import { useState } from "react";
import { ChevronDown, ChevronUp, Clock, Forward, Paperclip, Reply, ReplyAll, ShieldAlert, TriangleAlert } from "lucide-react";
import type { Message } from "@onemileauto/shared-types";
import { Badge, Card } from "@onemileauto/ui";
import { attachmentUrl } from "../../lib/api-client";
import { formatFullTimestamp, initialFor } from "../../lib/format";
import { SafeHtmlFrame } from "./safe-html-frame";

export type MessageComposeMode = "reply" | "forward";

interface MessageCardProps {
  message: Message;
  mailboxId: string;
  defaultExpanded: boolean;
  /**
   * Reply/Reply All/Forward are inline per-message actions (not a single
   * thread-level bar) so the user can pick exactly which message in the
   * thread they're acting on — see thread-detail.tsx, which owns the
   * actual ComposeSheet and just needs to know which message + mode.
   */
  onCompose: (mode: MessageComposeMode, message: Message, replyAll?: boolean) => void;
}

function bytesToLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MessageCard({ message, mailboxId, defaultExpanded, onCompose }: MessageCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const authFailed =
    message.auth.spfPass === false && message.auth.dkimPass === false && message.auth.dmarcPass === false;
  const isOutboundInFlight =
    message.direction === "outbound" && (message.sendState === "pending" || message.sendState === "sending");
  const isOutboundFailed = message.direction === "outbound" && message.sendState === "failed";
  const isOutbound = message.direction === "outbound";

  return (
    <Card
      className={`shrink-0 overflow-hidden bg-cream ${
        // shrink-0 is load-bearing, not decorative: this Card sits in a
        // flex-col scroll container (thread-detail.tsx), and because Card
        // sets overflow-hidden, Flexbox's "automatic minimum size" rule
        // lets a flex item shrink all the way to 0 instead of respecting
        // its content's height — so once a thread's total message height
        // exceeds the visible area, cards were silently squished/clipped
        // instead of the container just scrolling (which overflow-y-auto
        // on that container already handles correctly on its own).
        // Found while verifying this pass's changes against a real
        // multi-message thread.
        //
        // Sent vs. received needs to read at a glance, not just on close
        // inspection: a message we sent is indented in from the left with
        // a rust accent border (echoing the brand accent, not a loud new
        // color) and an accent-colored avatar; a received message sits
        // flush left with the neutral treatment — which also means a
        // single-message (received-only) thread renders exactly as before.
        isOutbound ? "ml-4 border-l-[3px] border-l-rust sm:ml-10" : ""
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
        aria-expanded={expanded}
      >
        <div
          className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
            isOutbound ? "bg-rust text-parchment" : "bg-parchment-dim text-muted"
          }`}
        >
          {initialFor([message.from])}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-semibold text-charcoal">
              {isOutbound ? "You" : message.from.name || message.from.email}
            </p>
            <div className="flex shrink-0 items-center gap-2 text-xs text-muted">
              {authFailed && (
                <span title="This message failed sender authentication checks (SPF/DKIM/DMARC)">
                  <ShieldAlert size={14} className="text-warning" />
                </span>
              )}
              {isOutboundInFlight && (
                <Badge variant="muted" className="gap-1">
                  <Clock size={11} /> Sending…
                </Badge>
              )}
              {isOutboundFailed && (
                <Badge variant="outline" className="gap-1 border-danger/40 text-danger">
                  <TriangleAlert size={11} /> Not sent
                </Badge>
              )}
              <span>{formatFullTimestamp(message.createdAt)}</span>
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>
          <p className="truncate text-xs text-muted">
            to {message.to.map((t) => t.name || t.email).join(", ") || "—"}
          </p>
          {!expanded && <p className="mt-1 truncate text-sm text-charcoal/70">{message.snippet}</p>}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border">
          {message.htmlBody ? (
            <SafeHtmlFrame html={message.htmlBody} attachments={message.attachments} mailboxId={mailboxId} />
          ) : (
            <div className="whitespace-pre-wrap break-words px-4 py-4 text-[15px] leading-relaxed text-charcoal">
              {message.textBody || "(no content)"}
            </div>
          )}

          {message.attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 border-t border-border px-4 py-3">
              {message.attachments.map((att) => (
                <a
                  key={att.id}
                  href={attachmentUrl(mailboxId, att.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-md border border-border bg-parchment-dim px-3 py-2 text-xs text-charcoal hover:border-border-strong"
                >
                  <Paperclip size={14} className="text-muted" />
                  <span className="max-w-[10rem] truncate">{att.filename}</span>
                  <span className="text-muted">{bytesToLabel(att.sizeBytes)}</span>
                </a>
              ))}
            </div>
          )}

          {/* Inline per-message actions — big, obvious, always-labeled
              buttons (not icon-only) so this stays easily tappable on
              mobile, matching the app's "no gesture/discovery-dependent
              actions" requirement for a non-technical user. A 3-column
              grid keeps all three buttons full-width and evenly sized at
              any viewport instead of overflowing on a narrow phone. */}
          <div className="grid grid-cols-3 gap-2 border-t border-border px-3 py-2.5">
            <button
              type="button"
              onClick={() => onCompose("reply", message, false)}
              className="flex h-10 items-center justify-center gap-1.5 rounded-md border border-border-strong text-xs font-medium text-charcoal transition-colors hover:bg-parchment-dim sm:text-sm"
            >
              <Reply size={16} />
              Reply
            </button>
            <button
              type="button"
              onClick={() => onCompose("reply", message, true)}
              className="flex h-10 items-center justify-center gap-1.5 rounded-md border border-border-strong text-xs font-medium text-charcoal transition-colors hover:bg-parchment-dim sm:text-sm"
            >
              <ReplyAll size={16} />
              Reply All
            </button>
            <button
              type="button"
              onClick={() => onCompose("forward", message)}
              className="flex h-10 items-center justify-center gap-1.5 rounded-md border border-border-strong text-xs font-medium text-charcoal transition-colors hover:bg-parchment-dim sm:text-sm"
            >
              <Forward size={16} />
              Forward
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
